import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { tap } from 'rxjs';
import {
  SiteLanguage,
  SiteLanguageResponse,
  SiteSettings,
  toSiteLanguage,
} from '../../dto/response/site.settings';
import { SiteSettingService } from '../../service/site-setting.service';
import { GetSiteSetting } from '../actions/siteSetting.action';

export interface SiteSettingStateModel {
  setting?: SiteSettings;
}

@State<SiteSettingStateModel>({
  name: 'siteSetting',
  defaults: {
    setting: {} as SiteSettings,
  },
})
@Injectable()
export class SiteSettingState {
  constructor(private siteSettingService: SiteSettingService) {}

  @Selector()
  static siteSetting(state: SiteSettingStateModel) {
    return state.setting;
  }

  // Idiomas de contenido del site (settings/v2 → language). Derivados en el selector,
  // no duplicados en el estado: state.setting sigue siendo la respuesta cruda del backend.
  @Selector()
  static defaultLanguage(state: SiteSettingStateModel): SiteLanguage | undefined {
    const raw = state.setting?.language?.default_language;
    return raw ? toSiteLanguage(raw) : undefined;
  }

  @Selector()
  static supportedLanguages(state: SiteSettingStateModel): SiteLanguage[] {
    const raw = state.setting?.language?.supported;
    return raw?.length ? raw.map(toSiteLanguage) : [];
  }

  // Idiomas SELECCIONABLES en el storefront = default ∪ supported (dedup por isoCode,
  // default primero). El backend modela el default aparte de `supported`, así que un site
  // con default=en y supported=[ar,es,fr] tiene 4 idiomas elegibles, no 3. Robusto también
  // si algún site incluyera el default dentro de `supported` (el dedup lo colapsa).
  @Selector()
  static availableLanguages(state: SiteSettingStateModel): SiteLanguage[] {
    const language = state.setting?.language;
    if (!language) return [];
    const seen = new Set<string>();
    const result: SiteLanguage[] = [];
    const push = (raw?: SiteLanguageResponse) => {
      if (!raw || seen.has(raw.iso_code)) return;
      seen.add(raw.iso_code);
      result.push(toSiteLanguage(raw));
    };
    push(language.default_language);
    (language.supported ?? []).forEach(push);
    return result;
  }

  @Action(GetSiteSetting)
  getTemplateConfig(ctx: StateContext<SiteSettingStateModel>) {
    return this.siteSettingService.fetchSiteSetting().pipe(
      tap({
        next: (result: any) => {
          const state = ctx.getState();
          ctx.setState({
            ...state,
            setting: result,
          });
        },
        error: (err) => {
          throw new Error(err?.error?.message);
        },
      }),
    );
  }
}
